// src/pages/customer/Checkout.jsx
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import { placeOrder, getOrderCharges, DEFAULT_ORDER_CHARGES, updateUserProfile, addUserAddress, updateUserAddress, deleteUserAddress } from "../../services/firestoreService";
import Spinner from "../../components/common/Spinner";
import toast from "react-hot-toast";
import { FiMapPin, FiPhone, FiUser, FiHome, FiChevronRight, FiLock, FiCheckCircle, FiPlus, FiCreditCard, FiTruck, FiEdit2, FiTrash2, FiX } from "react-icons/fi";



const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID;


const API_BASE_URL = "https://tripuraagro-backend.onrender.com" ;

// Loads the Razorpay checkout widget script once and caches the promise, so
// clicking "Place Order" more than once doesn't inject the script twice.
let razorpayScriptPromise = null;
const loadRazorpayScript = () => {
  if (window.Razorpay) return Promise.resolve(true);
  if (!razorpayScriptPromise) {
    razorpayScriptPromise = new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }
  return razorpayScriptPromise;
};

export default function Checkout() {
  const { items, total, clearCart } = useCart();
  const { user, userProfile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [placing, setPlacing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("COD"); // "COD" | "ONLINE"

  // Delivery-per-kg + SGST/CGST rates — set by the admin on the "Order Unit"
  // screen. Start with the known defaults so totals render immediately, then
  // swap in the live values once they've loaded from Firestore.
  const [charges, setCharges] = useState(DEFAULT_ORDER_CHARGES);

  useEffect(() => {
    getOrderCharges()
      .then(setCharges)
      .catch((err) => console.error("Failed to load order charges:", err));
  }, []);

  // Saved addresses live on userProfile.addresses (see firestoreService).
  // For anyone who saved an address before that feature existed, they'll
  // only have the older single address fields (userProfile.address/city/...)
  // — show that as one legacy entry so it isn't "lost".
  const savedAddresses = userProfile?.addresses?.length
    ? userProfile.addresses
    : (userProfile?.address
        ? [{
            id: "legacy",
            label: "Saved Address",
            address:  userProfile.address,
            city:     userProfile.city,
            state:    userProfile.state,
            pincode:  userProfile.pincode,
            landmark: userProfile.landmark,
            isDefault: true,
          }]
        : []);

  // Which address is currently chosen for this order: either the id of one
  // of savedAddresses, or "new" for the inline "add a new address" form.
  // AuthProvider only renders its children once the profile has finished
  // loading (see AuthContext), so userProfile is already settled by the
  // time this component's first render happens — a lazy initializer is
  // enough, no need to wait for it in an effect.
  const [selectedAddressId, setSelectedAddressId] = useState(() => {
    if (savedAddresses.length) {
      return (savedAddresses.find((a) => a.isDefault) || savedAddresses[0]).id;
    }
    return "new";
  });
  const [showNewAddressForm, setShowNewAddressForm] = useState(savedAddresses.length === 0);

  // Editing an existing saved address (inline form, separate from the
  // "add new address" flow above — saves immediately, not tied to placing
  // an order).
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const startEditAddress = (addr) => {
    setEditingAddressId(addr.id);
    setEditForm({
      label:    addr.label    || "",
      address:  addr.address  || "",
      city:     addr.city     || "",
      state:    addr.state    || "",
      pincode:  addr.pincode  || "",
      landmark: addr.landmark || "",
    });
  };

  const cancelEditAddress = () => {
    setEditingAddressId(null);
    setEditForm({});
  };

  const saveEditAddress = async () => {
    if (!editForm.address || !editForm.city || !editForm.state || !editForm.pincode) {
      toast.error("Address, city, state and pincode are required");
      return;
    }
    setSavingEdit(true);
    try {
      await updateUserAddress(user.uid, editingAddressId, editForm);
      await refreshProfile();
      toast.success("Address updated");
      cancelEditAddress();
    } catch (err) {
      console.error(err);
      toast.error("Could not update address. Please try again.");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteAddress = async (addr) => {
    if (!window.confirm(`Delete "${addr.label || "this address"}"?`)) return;
    setDeletingId(addr.id);
    try {
      await deleteUserAddress(user.uid, addr.id);
      await refreshProfile();
      if (selectedAddressId === addr.id) {
        setSelectedAddressId("new");
        setShowNewAddressForm(true);
      }
      toast.success("Address deleted");
    } catch (err) {
      console.error(err);
      toast.error("Could not delete address. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  const { register, handleSubmit, formState: { errors } } = useForm({
    // shouldUnregister: true so that when the "add a new address" fields are
    // hidden (an existing saved address is selected instead), their old
    // values/required-rules don't linger and block submission.
    shouldUnregister: true,
    defaultValues: {
      name:  userProfile?.name  || user?.displayName || "",
      phone: userProfile?.phone || "",
      email: user?.email || "",
      addressLabel: "", address: "", city: "", state: "", pincode: "", landmark: "",
      saveAddress: true,
    },
  });

  // Total kg this cart item represents, used for the per-kg delivery charge.
  // "kg" unit items store their per-unit weight in weightSize (e.g. weightSize
  // "5" + qty 3 = 15 kg). "pack" unit items are counted, not weighed, so they
  // don't add to delivery weight.
  const getItemKg = (item) => {
    const qty = Number(item.qty) || 0;
    if (!qty) return 0;
    if (item.unit === "pack") return 0;
    const weightNum = parseFloat(item.weightSize);
    return weightNum ? weightNum * qty : 0;
  };

  const totalKg       = items.reduce((sum, item) => sum + getItemKg(item), 0);
  const deliveryPerKg = Number(charges.deliveryPerKg) || 0;
  const sgstPercent   = Number(charges.sgstPercent) || 0;
  const cgstPercent   = Number(charges.cgstPercent) || 0;

  const deliveryCharge = +(totalKg * deliveryPerKg).toFixed(2);
  const sgstAmount      = +(total * (sgstPercent / 100)).toFixed(2);
  const cgstAmount      = +(total * (cgstPercent / 100)).toFixed(2);
  const grandTotal       = +(total + deliveryCharge + sgstAmount + cgstAmount).toFixed(2);

  // "30 kg" / "5 pack(s)" depending on the product's unit
  const getQtyLabel = (item) => {
    if (item.unit === "pack") return item.packSize ? `${item.packSize} pack(s)` : null;
    return item.weightSize ? `${item.weightSize} kg` : null;
  };

  // "₹107/kg" / "₹99/pack" depending on the product's unit
  const getRateLabel = (item) => {
    if (item.unit === "pack") return item.pricePerPack ? `₹${item.pricePerPack}/pack` : null;
    return item.pricePerKg ? `₹${item.pricePerKg}/kg` : null;
  };

  // Builds the order doc, writes it to Firestore, and syncs the profile
  // (name/phone + newly-typed address). Shared by both the COD path and the
  // Razorpay path (called only after payment is verified) so there's one
  // place that actually creates the order.
  const finalizeOrder = async (formData, shipAddress, paymentInfo) => {
    const orderDoc = {
      userId:      user.uid,
      userName:    formData.name,
      userEmail:   formData.email,
      userPhone:   formData.phone,
      shippingAddress: {
        address:  shipAddress.address,
        city:     shipAddress.city,
        state:    shipAddress.state,
        pincode:  shipAddress.pincode,
        landmark: shipAddress.landmark || "",
      },
      items: items.map((i) => ({
        id:            i.id,
        name:          i.name,
        price:         i.price,
        originalPrice: i.originalPrice || null,
        qty:           i.qty,
        image:         i.image,
        category:      i.category || "",
        unit:          i.unit || "",
        weightSize:    i.weightSize || null,
        pricePerKg:    i.pricePerKg || null,
        packSize:      i.packSize || null,
        pricePerPack:  i.pricePerPack || null,
        hsnSac:        i.hsnSac || "",
        lineTotal:     +(i.price * i.qty).toFixed(2),
      })),
      subtotal: total,
      totalKg,
      deliveryPerKg,
      deliveryCharge,
      sgstPercent,
      cgstPercent,
      sgstAmount,
      cgstAmount,
      total: grandTotal,
      ...paymentInfo, // { paymentMethod, paymentStatus, razorpayOrderId, razorpayPaymentId } etc.
    };
    const ref = await placeOrder(orderDoc);

    // Keep name/phone in sync with what was typed at checkout. (The new
    // address itself, if any, is already saved earlier in onSubmit — see
    // the comment there for why it can't wait until after the order.)
    try {
      await updateUserProfile(user.uid, { name: formData.name, phone: formData.phone });
      await refreshProfile();
    } catch (profileErr) {
      // Don't fail the whole order just because the profile save failed —
      // the order itself already went through.
      console.error("Failed to save name/phone to profile:", profileErr);
    }

    clearCart();
    navigate("/order-success", { state: { orderId: ref.id } });
  };

  const onSubmit = async (formData) => {
    if (items.length === 0) { toast.error("Cart is empty"); return; }

    // Resolve which address is actually being shipped to: one already
    // saved on the profile, or the one just typed into the "new address" form.
    let shipAddress;
    if (selectedAddressId !== "new") {
      shipAddress = savedAddresses.find((a) => a.id === selectedAddressId);
      if (!shipAddress) { toast.error("Please select a delivery address"); return; }
    } else {
      shipAddress = {
        address:  formData.address,
        city:     formData.city,
        state:    formData.state,
        pincode:  formData.pincode,
        landmark: formData.landmark,
      };

      // Save a brand-new address to the profile right away, rather than
      // waiting for the order to finish — otherwise a cancelled/failed
      // Razorpay payment (or any error before the order writes) means the
      // address the person just typed never shows up on My Account, even
      // though from their side they "added" it.
      if (formData.saveAddress) {
        try {
          await addUserAddress(user.uid, {
            label:    formData.addressLabel || "Address",
            address:  shipAddress.address,
            city:     shipAddress.city,
            state:    shipAddress.state,
            pincode:  shipAddress.pincode,
            landmark: shipAddress.landmark || "",
          });
          await refreshProfile();
        } catch (addrErr) {
          console.error("Failed to save new address to profile:", addrErr);
          toast.error("Could not save this address to your account, but you can still place the order.");
        }
      }
    }

    if (paymentMethod === "COD") {
      setPlacing(true);
      try {
        await finalizeOrder(formData, shipAddress, { paymentMethod: "COD" });
      } catch (err) {
        console.error(err);
        toast.error("Failed to place order. Please try again.");
      } finally {
        setPlacing(false);
      }
      return;
    }

    // ── Online payment (Razorpay) ──────────────────────────────────────────
    setPlacing(true);
    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error("Could not load payment gateway. Check your connection and try again.");
        setPlacing(false);
        return;
      }

      const idToken = await user.getIdToken();
      const createOrderRes = await fetch(`${API_BASE_URL}/api/create-order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ amount: grandTotal }),
      });
      const data = await createOrderRes.json();
      if (!createOrderRes.ok) {
        throw new Error(data?.error || "Could not create payment order.");
      }

      const razorpay = new window.Razorpay({
        key: RAZORPAY_KEY_ID,
        amount: data.amount,
        currency: data.currency,
        order_id: data.orderId,
        name: "Tripura Agro",
        description: "Order Payment",
        prefill: {
          name:    formData.name,
          email:   formData.email,
          contact: formData.phone,
        },
        theme: { color: "#3a5c2f" },
        handler: async (response) => {
          // Fires only on a successful payment. Verify it server-side
          // before writing the order — this is what stops someone from
          // faking a "payment success" from the browser console.
          try {
            const verifyIdToken = await user.getIdToken();
            const verifyRes = await fetch(`${API_BASE_URL}/api/verify-payment`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${verifyIdToken}`,
              },
              body: JSON.stringify({
                razorpay_order_id:   response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature:  response.razorpay_signature,
              }),
            });
            const verifyResult = await verifyRes.json();

            if (!verifyRes.ok || !verifyResult?.verified) {
              toast.error("Payment verification failed. If money was deducted, contact support.");
              setPlacing(false);
              return;
            }

            await finalizeOrder(formData, shipAddress, {
              paymentMethod:      "Razorpay (Online)",
              paymentStatus:      "Paid",
              razorpayOrderId:    response.razorpay_order_id,
              razorpayPaymentId:  response.razorpay_payment_id,
            });
          } catch (err) {
            console.error(err);
            toast.error("Payment succeeded but placing the order failed. Contact support with your payment ID.");
          } finally {
            setPlacing(false);
          }
        },
        modal: {
          // Fires if the user closes the popup without paying.
          ondismiss: () => setPlacing(false),
        },
      });

      razorpay.on("payment.failed", () => {
        toast.error("Payment failed. Please try again.");
        setPlacing(false);
      });

      razorpay.open();
    } catch (err) {
      console.error(err);
      toast.error("Could not start payment. Please try again.");
      setPlacing(false);
    }
  };

  // Reusable field component
  const Field = ({ label, name, icon: Icon, rules, type = "text", placeholder, colSpan }) => (
    <div className={colSpan}>
      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
        {label}
      </label>
      <div className="relative">
        {Icon && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
            <Icon className="w-4 h-4" />
          </span>
        )}
        <input
          {...register(name, rules)}
          type={type}
          placeholder={placeholder}
          className={`
            w-full rounded-xl border bg-gray-50 text-sm text-gray-900 placeholder-gray-400
            px-3 py-2.5 transition-all outline-none
            focus:bg-white focus:border-primary-400 focus:ring-2 focus:ring-primary-100
            ${Icon ? "pl-9" : ""}
            ${errors[name]
              ? "border-red-300 bg-red-50 focus:border-red-400 focus:ring-red-100"
              : "border-gray-200 hover:border-gray-300"
            }
          `}
        />
      </div>
      {errors[name] && (
        <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
          <span className="w-3 h-3 inline-block">⚠</span> {errors[name].message}
        </p>
      )}
    </div>
  );

  // Section card wrapper
  const Section = ({ step, title, icon: Icon, children }) => (
    <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-50">
        <div className="w-7 h-7 rounded-lg bg-primary-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
          {step}
        </div>
        <h2 className="font-semibold text-gray-900 text-sm flex items-center gap-2">
          <Icon className="w-4 h-4 text-primary-500" /> {title}
        </h2>
      </div>
      <div className="p-5">
        {children}
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

      <Helmet>
        <title>Checkout | Tripura Agro</title>
        <meta
          name="description"
          content="Tripura Agro Industries was established in 2022 with the goal of leveraging supply and demand between farmers and Mandis at the national level and the final customer, who may be an individual or an institution."
        />
      </Helmet>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 text-xs text-gray-400 mb-2">
          <span>Cart</span>
          <FiChevronRight className="w-3 h-3" />
          <span className="text-primary-600 font-semibold">Checkout</span>
          <FiChevronRight className="w-3 h-3" />
          <span>Confirmation</span>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">Complete Your Order</h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

          {/* Left: Form sections */}
          <div className="lg:col-span-7 space-y-4">

            {/* Contact */}
            <Section step="1" title="Contact Details" icon={FiUser}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Full Name *"  name="name"  icon={FiUser}  rules={{ required: "Name is required" }}  placeholder="John Doe" />
                <Field label="Phone *"      name="phone" icon={FiPhone} rules={{ required: "Phone is required", pattern: { value: /^[0-9]{10}$/, message: "Enter valid 10-digit number" } }} placeholder="9876543210" />
                <Field label="Email *" name="email" type="email" rules={{ required: "Email is required" }} placeholder="john@example.com" colSpan="sm:col-span-2" />
              </div>
            </Section>

            {/* Address */}
            <Section step="2" title="Delivery Address" icon={FiMapPin}>
              {/* Saved addresses to pick from */}
              {savedAddresses.length > 0 && (
                <div className="space-y-3 mb-4">
                  {savedAddresses.map((addr) => {
                    const isChosen = selectedAddressId === addr.id && !showNewAddressForm;
                    const isEditing = editingAddressId === addr.id;

                    if (isEditing) {
                      return (
                        <div key={addr.id} className="p-4 rounded-xl border border-primary-300 bg-primary-50/40 space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Edit Address</span>
                            <button type="button" onClick={cancelEditAddress} className="text-gray-400 hover:text-gray-600">
                              <FiX className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <input value={editForm.label} onChange={(e) => setEditForm({ ...editForm, label: e.target.value })}
                              placeholder="Label (Home, Office, etc.)" className="sm:col-span-2 rounded-lg border border-gray-200 bg-white text-sm px-3 py-2 outline-none focus:border-primary-400" />
                            <input value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                              placeholder="Street Address *" className="sm:col-span-2 rounded-lg border border-gray-200 bg-white text-sm px-3 py-2 outline-none focus:border-primary-400" />
                            <input value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                              placeholder="City *" className="rounded-lg border border-gray-200 bg-white text-sm px-3 py-2 outline-none focus:border-primary-400" />
                            <input value={editForm.state} onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                              placeholder="State *" className="rounded-lg border border-gray-200 bg-white text-sm px-3 py-2 outline-none focus:border-primary-400" />
                            <input value={editForm.pincode} onChange={(e) => setEditForm({ ...editForm, pincode: e.target.value })}
                              placeholder="Pincode *" className="rounded-lg border border-gray-200 bg-white text-sm px-3 py-2 outline-none focus:border-primary-400" />
                            <input value={editForm.landmark} onChange={(e) => setEditForm({ ...editForm, landmark: e.target.value })}
                              placeholder="Landmark (optional)" className="rounded-lg border border-gray-200 bg-white text-sm px-3 py-2 outline-none focus:border-primary-400" />
                          </div>
                          <div className="flex gap-2 justify-end">
                            <button type="button" onClick={cancelEditAddress} className="text-xs font-semibold text-gray-500 hover:text-gray-700 px-3 py-2">
                              Cancel
                            </button>
                            <button type="button" disabled={savingEdit} onClick={saveEditAddress}
                              className="text-xs font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-lg px-4 py-2 disabled:opacity-60">
                              {savingEdit ? "Saving..." : "Save Address"}
                            </button>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <label
                        key={addr.id}
                        className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                          isChosen ? "border-primary-400 bg-primary-50" : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <input
                          type="radio"
                          name="savedAddressChoice"
                          className="mt-1 accent-primary-600"
                          checked={isChosen}
                          onChange={() => { setSelectedAddressId(addr.id); setShowNewAddressForm(false); }}
                        />
                        <div className="flex-1 text-sm min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-gray-900">{addr.label || "Address"}</span>
                            {addr.isDefault && (
                              <span className="text-[10px] uppercase tracking-wide font-bold bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded">
                                Default
                              </span>
                            )}
                          </div>
                          <p className="text-gray-500 mt-0.5">
                            {addr.address}, {addr.city}, {addr.state} - {addr.pincode}
                            {addr.landmark ? ` (${addr.landmark})` : ""}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); startEditAddress(addr); }}
                            className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-white rounded-lg transition-colors"
                            title="Edit address"
                          >
                            <FiEdit2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            disabled={deletingId === addr.id}
                            onClick={(e) => { e.preventDefault(); handleDeleteAddress(addr); }}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-white rounded-lg transition-colors disabled:opacity-50"
                            title="Delete address"
                          >
                            <FiTrash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}

              {/* Toggle to add a brand-new address */}
              <button
                type="button"
                onClick={() => { setShowNewAddressForm(true); setSelectedAddressId("new"); }}
                className={`text-sm font-semibold flex items-center gap-1.5 ${
                  showNewAddressForm ? "text-primary-700" : "text-primary-600 hover:text-primary-700"
                } ${savedAddresses.length > 0 ? "mb-4" : ""}`}
              >
                <FiPlus className="w-4 h-4" /> Add a new address
              </button>

              {/* New address form */}
              {showNewAddressForm && (
                <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${savedAddresses.length > 0 ? "pt-4 border-t border-gray-100" : ""}`}>
                  <Field label="Address Label (optional)" name="addressLabel" placeholder="Home, Office, etc." colSpan="sm:col-span-2" />
                  <Field label="Street Address *" name="address" icon={FiHome} rules={{ required: "Address is required" }} placeholder="House no, Street name" colSpan="sm:col-span-2" />
                  <Field label="City *"    name="city"    rules={{ required: "City is required" }}    placeholder="Mumbai" />
                  <Field label="State *"   name="state"   rules={{ required: "State is required" }}   placeholder="Maharashtra" />
                  <Field label="Pincode *" name="pincode" rules={{ required: "Pincode is required", pattern: { value: /^[0-9]{6}$/, message: "Enter valid 6-digit pincode" } }} placeholder="400001" />
                  <Field label="Landmark (optional)" name="landmark" placeholder="Near park, etc." />
                  <label className="sm:col-span-2 flex items-center gap-2 text-xs text-gray-500 -mt-1">
                    <input type="checkbox" {...register("saveAddress")} defaultChecked className="rounded accent-primary-600" />
                    Save this address for future orders
                  </label>
                </div>
              )}
            </Section>

            {/* Payment */}
            <Section step="3" title="Payment Method" icon={FiLock}>
              <div className="space-y-3">
                <label
                  className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all ${
                    paymentMethod === "COD" ? "border-primary-400 bg-primary-50" : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="paymentMethod"
                    className="accent-primary-600"
                    checked={paymentMethod === "COD"}
                    onChange={() => setPaymentMethod("COD")}
                  />
                  <div className="w-11 h-11 bg-white border border-primary-100 rounded-xl flex items-center justify-center text-xl shadow-sm flex-shrink-0">
                    <FiTruck className="w-5 h-5 text-primary-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 text-sm">Cash on Delivery</p>
                    <p className="text-xs text-gray-400 mt-0.5">Pay when your order arrives at your door</p>
                  </div>
                </label>

                <label
                  className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all ${
                    paymentMethod === "ONLINE" ? "border-primary-400 bg-primary-50" : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="paymentMethod"
                    className="accent-primary-600"
                    checked={paymentMethod === "ONLINE"}
                    onChange={() => setPaymentMethod("ONLINE")}
                  />
                  <div className="w-11 h-11 bg-white border border-primary-100 rounded-xl flex items-center justify-center text-xl shadow-sm flex-shrink-0">
                    <FiCreditCard className="w-5 h-5 text-primary-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 text-sm">Pay Online</p>
                    <p className="text-xs text-gray-400 mt-0.5">Card, UPI, Netbanking &amp; wallets via Razorpay</p>
                  </div>
                </label>
              </div>
              <div className="flex items-center gap-2 mt-3 text-xs text-gray-400">
                <FiLock className="w-3 h-3" />
                <span>Your information is encrypted and secure</span>
              </div>
            </Section>
          </div>

          {/* Right: Order Summary */}
          <div className="lg:col-span-5">
            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm sticky top-24 overflow-hidden">

              {/* Summary header */}
              <div className="px-5 py-4 border-b border-gray-50">
                <h3 className="font-bold text-gray-900 text-sm">Order Summary</h3>
                <p className="text-xs text-gray-400 mt-0.5">{items.length} item{items.length !== 1 ? "s" : ""}</p>
              </div>

              {/* Items list */}
              <div className="px-5 py-3 space-y-3 max-h-56 overflow-y-auto">
                {items.map((item) => (
                  <div key={item.id} className="flex gap-3 items-center">
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-50 border border-gray-100 flex-shrink-0">
                      {item.image
                        ? <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center text-base">🌱</div>
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-900 truncate">{item.name}</p>
                      <p className="text-xs text-gray-400">
                        Qty: {item.qty}
                        {getQtyLabel(item) ? ` · ${getQtyLabel(item)}` : (item.unit ? ` · per ${item.unit}` : "")}
                        {getRateLabel(item) ? ` · ${getRateLabel(item)}` : ""}
                      </p>
                    </div>
                    <span className="text-xs font-bold text-gray-800 flex-shrink-0">
                      ₹{(item.price * item.qty).toFixed(0)}
                    </span>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="px-5 py-4 border-t border-gray-50 space-y-2.5">
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Subtotal</span>
                  <span className="font-medium text-gray-700">₹{total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Delivery Charge{totalKg > 0 ? ` (${totalKg.toFixed(2)} kg)` : ""}</span>
                  <span className="font-semibold text-gray-700">₹{deliveryCharge.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs text-gray-500">
                  <span>SGST ({sgstPercent}%)</span>
                  <span className="font-medium text-gray-700">₹{sgstAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs text-gray-500">
                  <span>CGST ({cgstPercent}%)</span>
                  <span className="font-medium text-gray-700">₹{cgstAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                  <span className="font-bold text-gray-900 text-sm">Total</span>
                  <span className="font-bold text-xl text-primary-600">₹{grandTotal.toFixed(2)}</span>
                </div>
              </div>

              {/* CTA */}
              <div className="px-5 pb-5">
                <button
                  type="submit"
                  disabled={placing}
                  className="btn-primary w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {placing ? (
                    <><Spinner size="sm" /> {paymentMethod === "COD" ? "Placing Order..." : "Processing Payment..."}</>
                  ) : (
                    <><FiCheckCircle className="w-4 h-4" /> {paymentMethod === "COD" ? "Place Order (COD)" : `Pay ₹${grandTotal.toFixed(2)} Online`}</>
                  )}
                </button>
                <p className="text-center text-xs text-gray-400 mt-3">
                  No Return, Refund, or Replacement. For any issues, please contact the Tripura Agro office.
                </p>
              </div>
            </div>
          </div>

        </div>
      </form>
    </div>
  );
}